import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  CheckCircleIcon,
  CalendarIcon,
  UsersIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  PhoneIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';

interface Booking {
  _id: string;
  bookingCode: string;
  status: string;
  amount: number;
  currency: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: {
      countryCode: string;
      number: string;
    };
  };
  package: {
    _id: string;
    name: string;
    packageCode: string;
    validityPeriod: number;
  };
  voucherExpiryDate: string;
  createdAt: string;
}

const BookingConfirmationPage: React.FC = () => {
  const { bookingCode } = useParams<{ bookingCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isQueryParamMode, setIsQueryParamMode] = useState(false);
  const [queryData, setQueryData] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    // Check if we're in query parameter mode (redirect from payment)
    const status = searchParams.get('status');
    const reference = searchParams.get('reference');
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const amount = searchParams.get('amount');
    const currency = searchParams.get('currency');

    if (status && reference && !bookingCode) {
      // This is a redirect from payment - attempt to create booking
      const packageId = searchParams.get('packageId');
      const phone = searchParams.get('phone');

      setIsQueryParamMode(true);
      setQueryData({
        status,
        reference,
        email,
        name: name?.replace('%20', ' '),
        amount: parseFloat(amount || '0'),
        currency,
        packageId,
        phone
      });
      setLoading(false);
      return;
    }

    // Original booking lookup functionality
    const fetchBooking = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bookings/code/${bookingCode}`
        );
        const data = response.data;
        setBooking(data.booking || data);
      } catch (error) {
        console.error('Failed to fetch booking:', error);
      } finally {
        setLoading(false);
      }
    };

    if (bookingCode) {
      fetchBooking();
    }
  }, [bookingCode, searchParams]);

  // Helper to parse phone into countryCode and number
  const parsePhone = (raw?: string): { countryCode: string; number: string } => {
    if (!raw) return { countryCode: '+1', number: '' };
    const cleaned = raw.replace(/\s|-/g, '');
    if (cleaned.startsWith('+')) {
      // Extract country code as +<1-3 digits>
      const match = cleaned.match(/^(\+\d{1,3})(\d+)$/);
      if (match) return { countryCode: match[1], number: match[2] };
      return { countryCode: '+1', number: cleaned.replace('+', '') };
    }
    return { countryCode: '+1', number: cleaned };
  };

  // When arriving from payment gateway, create booking in backend
  useEffect(() => {
    const createBookingFromQuery = async () => {
      if (!isQueryParamMode || !queryData) return;
      if (!queryData.packageId) {
        setCreateError('Missing package information; cannot finalize booking automatically.');
        return;
      }
      try {
        setCreating(true);
        setCreateError(null);
        const [firstName, ...rest] = (queryData.name || '').split(' ');
        const lastName = rest.join(' ') || '';
        const phoneParsed = parsePhone(queryData.phone);
        const body = {
          customer: {
            firstName: firstName || 'Guest',
            lastName: lastName || 'Customer',
            email: queryData.email,
            phone: phoneParsed
          },
          package: queryData.packageId,
          currency: (queryData.currency || 'USD').toUpperCase(),
          amount: queryData.amount || 0,
          paymentDetails: {
            transactionId: queryData.reference,
            paymentMethod: 'Flywire',
            paymentStatus: 'completed'
          }
        };
        const res = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bookings`, body);
        const created = res.data?.booking || res.data;
        if (created?.bookingCode) {
          // Navigate to canonical route
          navigate(`/booking-confirmation/${created.bookingCode}`, { replace: true });
        }
      } catch (err: any) {
        console.error('Failed to create booking from payment redirect:', err?.response?.data || err?.message || err);
        setCreateError(err?.response?.data?.message || 'Failed to finalize your booking automatically.');
      } finally {
        setCreating(false);
      }
    };

    createBookingFromQuery();
  }, [isQueryParamMode, queryData, navigate]);

  const copyBookingCode = () => {
    if (booking) {
      navigator.clipboard.writeText(booking.bookingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking confirmation...</p>
        </div>
      </div>
    );
  }

  // Handle query parameter mode (payment redirect)
  if (isQueryParamMode && queryData) {
    return (
      <>
        <Helmet>
          <title>Payment Successful - Thank You | Bali Getaway</title>
        </Helmet>
        
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-8">
          <div className="container mx-auto px-4">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <CheckCircleIcon className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">🎉 Payment Successful!</h1>
              <p className="text-gray-600 text-xl">
                Thank you for choosing Bali Getaway! Your voucher is being processed.
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              {/* Payment Summary Card */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Payment Confirmation</h2>
                  <p className="text-orange-100">Transaction completed successfully</p>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Transaction Reference
                      </label>
                      <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-lg font-bold">
                        {queryData.reference}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount Paid
                      </label>
                      <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
                        <span className="text-2xl font-bold text-green-600">
                          {queryData.currency} ${queryData.amount?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    
                    {queryData.email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <p className="text-gray-900 font-medium">{queryData.email}</p>
                      </div>
                    )}
                    
                    {queryData.name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer Name
                        </label>
                        <p className="text-gray-900 font-medium">{queryData.name}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* What's Next */}
              <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">What Happens Next?</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Confirmation Email</p>
                      <p className="text-gray-600">
                        We're sending your voucher and booking details to {queryData.email || 'your email'}. This may take a few minutes.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Team Contact</p>
                      <p className="text-gray-600">
                        Our travel specialists will contact you within 24 hours to help plan your perfect Bali getaway.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Resort Selection</p>
                      <p className="text-gray-600">
                        Choose from our premium partner resorts and book your preferred travel dates.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Booking Finalization Status */}
                <div className="bg-white rounded-xl shadow p-6 mb-8">
                  {creating ? (
                    <p className="text-gray-700">Finalizing your booking and sending confirmation emails...</p>
                  ) : createError ? (
                    <div>
                      <p className="text-red-600 font-semibold mb-2">We couldn't finalize your booking automatically.</p>
                      <p className="text-gray-600">Our team will contact you shortly, or you can reach us at support@baligetaway.com.</p>
                    </div>
                  ) : (
                    <p className="text-gray-700">Redirecting to your booking confirmation...</p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/packages"
                  className="px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors text-center"
                >
                  Browse More Packages
                </Link>
                <Link
                  to="/booking-lookup"
                  className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold rounded-xl transition-colors text-center"
                >
                  Track Your Booking
                </Link>
              </div>

              {/* Support Info */}
              <div className="mt-8 p-6 bg-orange-50 rounded-xl text-center">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Need Assistance?</h4>
                <p className="text-gray-600 mb-4">
                  Our customer support team is here to help you with any questions about your booking.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-orange-700">
                  <a href="mailto:support@baligetaway.com" className="font-bold hover:text-orange-600">
                    📧 support@baligetaway.com
                  </a>
                  <a href="tel:+1-800-BALI-GET" className="font-bold hover:text-orange-600">
                    📞 +1-800-BALI-GET
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking not found</h2>
          <p className="text-gray-600 mb-4">The booking code you provided doesn't exist.</p>
          <Link to="/booking-lookup" className="btn-primary">
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Booking Confirmation - {booking.bookingCode} | Bali Getaway</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
            <p className="text-gray-600 text-lg">
              Thank you for your purchase. Your Bali getaway voucher is ready!
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Booking Details Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4">
                <div className="flex items-center justify-between text-white">
                  <div>
                    <h2 className="text-xl font-bold">Booking Confirmation</h2>
                    <p className="text-orange-100">Keep this confirmation for your records</p>
                  </div>
                  <button
                    onClick={handlePrint}
                    className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
                  >
                    <PrinterIcon className="h-5 w-5" />
                    <span>Print</span>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Booking Code */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Booking Code
                      </label>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-lg font-bold text-center">
                          {booking.bookingCode}
                        </div>
                        <button
                          onClick={copyBookingCode}
                          className="px-3 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg transition-colors"
                          title="Copy booking code"
                        >
                          <DocumentDuplicateIcon className="h-5 w-5 text-gray-600" />
                        </button>
                      </div>
                      {copied && (
                        <p className="text-sm text-green-600 mt-1">Copied to clipboard!</p>
                      )}
                    </div>

                    {/* Package Details */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Package Details
                      </label>
                      <div className="space-y-2">
                        <p className="text-lg font-semibold text-gray-900">{booking.package.name}</p>
                        <p className="text-gray-600">Package Code: {booking.package.packageCode}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <CalendarIcon className="h-4 w-4" />
                            <span>Valid for {booking.package.validityPeriod} months</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Customer Details */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Customer Details
                      </label>
                      <div className="space-y-2">
                        <p className="font-semibold text-gray-900">
                          {booking.customer.firstName} {booking.customer.lastName}
                        </p>
                        <div className="flex items-center space-x-2 text-gray-600">
                          <EnvelopeIcon className="h-4 w-4" />
                          <span>{booking.customer.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-600">
                          <PhoneIcon className="h-4 w-4" />
                          <span>
                            {booking.customer.phone.countryCode}{booking.customer.phone.number}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Payment Summary */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Summary
                      </label>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Package Price</span>
                            <span className="font-semibold">
                              {booking.currency} {booking.amount}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                            <span className="text-lg font-bold text-gray-900">Total Paid</span>
                            <span className="text-xl font-bold text-green-600">
                              {booking.currency} {booking.amount}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Important Dates */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Important Dates
                      </label>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Booking Date</span>
                          <span className="font-medium">
                            {new Date(booking.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Voucher Expires</span>
                          <span className="font-medium text-orange-600">
                            {new Date(booking.voucherExpiryDate).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Confirmed
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What's Next?</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Check your email</p>
                    <p className="text-gray-600 text-sm">
                      We've sent a detailed confirmation email to {booking.customer.email}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">We'll contact you soon</p>
                    <p className="text-gray-600 text-sm">
                      Our team will reach out within 24 hours to help you select your resort and finalize travel dates
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Enjoy your trip!</p>
                    <p className="text-gray-600 text-sm">
                      Once everything is confirmed, you can relax and look forward to your amazing Bali experience
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/packages"
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors text-center"
              >
                Browse More Packages
              </Link>
              <Link
                to="/booking-lookup"
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg transition-colors text-center"
              >
                Lookup Another Booking
              </Link>
              <a
                href="mailto:hello@baligetaway.com"
                className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors text-center"
              >
                Contact Support
              </a>
            </div>

            {/* Contact Information */}
            <div className="mt-8 p-6 bg-orange-50 rounded-lg text-center">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h4>
              <p className="text-gray-600 mb-4">
                Our customer support team is here to help you with any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="flex items-center space-x-2 text-gray-700">
                  <EnvelopeIcon className="h-5 w-5" />
                  <span>hello@baligetaway.com</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-700">
                  <PhoneIcon className="h-5 w-5" />
                  <span>+1-800-BALI-GET</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingConfirmationPage;
